import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ensureSpoonacularTagEnrichment,
  getSpoonacularTagEnrichmentStatus,
  SpoonacularTagClassificationStatusItem,
  SpoonacularTagEnrichmentItem,
  SpoonacularTagEnrichmentItemType,
} from '@/lib/apiService';

type SearchItem = Record<string, unknown>;

const PENDING_STATUSES = new Set(['not_cached', 'queued', 'processing']);
const TERMINAL_STATUSES = new Set(['completed', 'failed']);
const VALID_STATUSES = new Set([...PENDING_STATUSES, ...TERMINAL_STATUSES]);
const DEFAULT_VISIBLE_BATCH_SIZE = 30;
const MAX_TRACKED_ITEMS = 50;
const ENSURE_BATCH_SIZE = 10;
const ENSURE_BATCH_INTERVAL_MS = 1500;
const INITIAL_POLL_DELAY_MS = 1000;
const POLL_INTERVAL_MS = 3000;

const normalizeSourceId = (value: unknown): string => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const withoutPrefix = raw.replace(/^spoonacular[_:-]/i, '').trim();
  return /^\d+$/.test(withoutPrefix) ? withoutPrefix : '';
};

export const resolveSpoonacularTagEnrichmentItem = (
  item: unknown,
): SpoonacularTagEnrichmentItem | null => {
  if (!item || typeof item !== 'object') return null;
  const record = item as SearchItem;
  const provider = String(record.provider ?? '').trim().toLowerCase();
  const source = String(record.source ?? record._rawSource ?? '').trim().toLowerCase();
  const hasSpoonacularIdentity = provider === 'spoonacular'
    || source === 'spoonacular'
    || String(record.id ?? '').startsWith('spoonacular_')
    || String(record.spoonacularId ?? record.spoonacularIngredientId ?? '').trim().length > 0;

  if (!hasSpoonacularIdentity) return null;

  const itemTypeRaw = String(record.type ?? record._searchItemType ?? '').trim().toLowerCase();
  const itemType: SpoonacularTagEnrichmentItemType = itemTypeRaw === 'recipe' ? 'recipe' : 'ingredient';
  const sourceId = normalizeSourceId(
    record.sourceId
    ?? record._sourceId
    ?? record.spoonacularId
    ?? record.spoonacularIngredientId
    ?? record.id,
  );

  return sourceId ? { itemType, sourceId } : null;
};

const keyForItem = (item: SpoonacularTagEnrichmentItem): string => `${item.itemType}:${item.sourceId}`;

const normalizeStatus = (value: unknown): string => String(value ?? '').trim().toLowerCase();

const stringArray = (value: unknown): string[] => (
  Array.isArray(value)
    ? value.map((entry) => String(entry ?? '').trim()).filter(Boolean)
    : []
);

const statusFromRawItem = (item: SearchItem): SpoonacularTagClassificationStatusItem | null => {
  const enrichmentItem = resolveSpoonacularTagEnrichmentItem(item);
  if (!enrichmentItem) return null;

  const status = normalizeStatus(item.tagClassificationStatus ?? item.status);
  if (!VALID_STATUSES.has(status)) return null;

  return {
    ...enrichmentItem,
    status: status as SpoonacularTagClassificationStatusItem['status'],
    tagClassificationStatus: status as SpoonacularTagClassificationStatusItem['tagClassificationStatus'],
    tagClassificationSource: item.tagClassificationSource ?? 'preview',
    tagClassificationUpdatedAt: item.tagClassificationUpdatedAt ?? null,
    allergenTagIds: stringArray(item.allergenTagIds ?? item.allergens),
    intoleranceTagIds: stringArray(item.intoleranceTagIds ?? item.intoleranceTags),
    dietaryTagIds: stringArray(item.dietaryTagIds ?? item.dietLabels ?? item.tags),
    allergens: stringArray(item.allergens ?? item.allergenTagIds),
    intoleranceTags: stringArray(item.intoleranceTags ?? item.intoleranceTagIds),
    dietLabels: stringArray(item.dietLabels ?? item.dietaryTagIds ?? item.tags),
    isVegan: typeof item.isVegan === 'boolean' ? item.isVegan : null,
    isVegetarian: typeof item.isVegetarian === 'boolean' ? item.isVegetarian : null,
    isGlutenFree: typeof item.isGlutenFree === 'boolean' ? item.isGlutenFree : null,
    isDairyFree: typeof item.isDairyFree === 'boolean' ? item.isDairyFree : null,
  };
};

const findRawItemForKey = <T extends SearchItem>(items: T[], key: string): T | undefined => (
  items.find((candidate) => {
    const resolved = resolveSpoonacularTagEnrichmentItem(candidate);
    return resolved ? keyForItem(resolved) === key : false;
  })
);

const mergeCompletedStatus = <T extends SearchItem>(
  item: T,
  status: SpoonacularTagClassificationStatusItem,
): T => {
  const base: SearchItem = {
    ...item,
    tagClassificationStatus: status.status,
    tagClassificationSource: status.tagClassificationSource,
    tagClassificationUpdatedAt: status.tagClassificationUpdatedAt,
  };

  if (status.status !== 'completed') {
    return base as T;
  }

  return {
    ...base,
    allergens: status.allergenTagIds,
    allergenTagIds: status.allergenTagIds,
    intoleranceTags: status.intoleranceTagIds,
    intoleranceTagIds: status.intoleranceTagIds,
    dietaryTagIds: status.dietaryTagIds,
    dietLabels: status.itemType === 'recipe' ? status.dietLabels : status.dietaryTagIds,
    tags: status.dietaryTagIds,
    isVegan: status.isVegan ?? false,
    isVegetarian: status.isVegetarian ?? false,
    isGlutenFree: status.isGlutenFree ?? false,
    isDairyFree: status.isDairyFree ?? false,
  } as T;
};

export function useSpoonacularTagEnrichment<T extends SearchItem>(
  items: T[],
  options: { enabled?: boolean; maxItems?: number } = {},
): T[] {
  const enabled = options.enabled ?? true;
  const maxItems = Math.max(
    1,
    Math.min(Math.floor(Number(options.maxItems ?? DEFAULT_VISIBLE_BATCH_SIZE) || DEFAULT_VISIBLE_BATCH_SIZE), MAX_TRACKED_ITEMS),
  );
  const [statuses, setStatuses] = useState<Record<string, SpoonacularTagClassificationStatusItem>>({});
  const statusesRef = useRef<Record<string, SpoonacularTagClassificationStatusItem>>({});
  const itemsRef = useRef<T[]>(items);
  const enrichmentItemsRef = useRef<SpoonacularTagEnrichmentItem[]>([]);
  const ensuredKeysRef = useRef<Set<string>>(new Set());
  const ensureInFlightRef = useRef(false);
  const pollInFlightRef = useRef(false);
  const pollSequenceRef = useRef(0);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    statusesRef.current = statuses;
  }, [statuses]);

  const enrichmentItems = useMemo(() => {
    const map = new Map<string, SpoonacularTagEnrichmentItem>();
    for (const item of items) {
      const enrichmentItem = resolveSpoonacularTagEnrichmentItem(item);
      if (enrichmentItem) {
        map.set(keyForItem(enrichmentItem), enrichmentItem);
        if (map.size >= maxItems) {
          break;
        }
      }
    }
    return [...map.values()];
  }, [items, maxItems]);

  useEffect(() => {
    enrichmentItemsRef.current = enrichmentItems;
  }, [enrichmentItems]);

  const itemKeys = useMemo(
    () => enrichmentItems.map(keyForItem).join('|'),
    [enrichmentItems],
  );

  useEffect(() => {
    if (!enabled || !itemKeys) return;

    let cancelled = false;

    const ensureNextBatch = () => {
      if (cancelled || ensureInFlightRef.current) return;

      const currentItems = enrichmentItemsRef.current;
      const rawItems = itemsRef.current;
      const candidates = currentItems
        .filter((item) => {
          const key = keyForItem(item);
          if (ensuredKeysRef.current.has(key)) return false;
          const rawItem = findRawItemForKey(rawItems, key);
          const rawStatus = normalizeStatus(rawItem?.tagClassificationStatus);
          const polledStatus = normalizeStatus(statusesRef.current[key]?.status);
          const effectiveStatus = polledStatus || rawStatus;
          return !TERMINAL_STATUSES.has(effectiveStatus);
        })
        .slice(0, Math.min(ENSURE_BATCH_SIZE, maxItems));

      if (candidates.length === 0) return;

      const candidateKeys = candidates.map(keyForItem);
      candidateKeys.forEach((key) => ensuredKeysRef.current.add(key));
      ensureInFlightRef.current = true;
      ensureSpoonacularTagEnrichment(candidates)
        .then((response) => {
          if (cancelled || !response?.items?.length) return;
          setStatuses((prev) => {
            const next = { ...prev };
            for (const status of response.items) {
              next[keyForItem(status)] = status;
            }
            return next;
          });
        })
        .catch((error) => {
          candidateKeys.forEach((key) => ensuredKeysRef.current.delete(key));
          if (!cancelled) {
            console.warn('[SpoonacularTags] Failed to ensure tag enrichment:', error);
          }
        })
        .finally(() => {
          ensureInFlightRef.current = false;
        });
    };

    const timeout = window.setTimeout(ensureNextBatch, 350);
    const interval = window.setInterval(ensureNextBatch, ENSURE_BATCH_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
      window.clearInterval(interval);
      ensureInFlightRef.current = false;
    };
  }, [enabled, itemKeys, maxItems]);

  useEffect(() => {
    if (!enabled || !itemKeys) return;

    let cancelled = false;
    const sequence = ++pollSequenceRef.current;

    const poll = () => {
      if (cancelled || pollInFlightRef.current) return;

      const currentItems = enrichmentItemsRef.current;
      const rawItems = itemsRef.current;
      const pendingItems = currentItems.filter((item) => {
        const key = keyForItem(item);
        const rawItem = findRawItemForKey(rawItems, key);
        const status = normalizeStatus(statusesRef.current[key]?.status ?? rawItem?.tagClassificationStatus);
        return PENDING_STATUSES.has(status);
      });

      if (pendingItems.length === 0) return;

      pollInFlightRef.current = true;
      getSpoonacularTagEnrichmentStatus(pendingItems.slice(0, Math.min(ENSURE_BATCH_SIZE, maxItems)))
        .then((response) => {
          if (cancelled || sequence !== pollSequenceRef.current || !response?.items?.length) return;
          setStatuses((prev) => {
            const next = { ...prev };
            for (const status of response.items) {
              next[keyForItem(status)] = status;
            }
            return next;
          });
        })
        .catch((error) => {
          if (!cancelled) {
            console.warn('[SpoonacularTags] Failed to poll tag enrichment status:', error);
          }
        })
        .finally(() => {
          if (sequence === pollSequenceRef.current) {
            pollInFlightRef.current = false;
          }
        });
    };

    const initialTimeout = window.setTimeout(poll, INITIAL_POLL_DELAY_MS);
    const interval = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(initialTimeout);
      window.clearInterval(interval);
      pollInFlightRef.current = false;
    };
  }, [enabled, itemKeys, maxItems]);

  return useMemo(() => (
    items.map((item) => {
      const enrichmentItem = resolveSpoonacularTagEnrichmentItem(item);
      if (!enrichmentItem) return item;
      const key = keyForItem(enrichmentItem);
      const status = statuses[key] ?? statusFromRawItem(item);
      return status ? mergeCompletedStatus(item, status) : item;
    })
  ), [items, statuses]);
}
